import { APPENV } from '@/config/env';
import { Drawer } from '@/components/drawer';
import {
  useAppRoleList,
  useAssignRole,
  useCreateAppRole,
  useCreateUser
} from '@/features/users/hooks';
import { DrawerSizeEnum, PlacementEnum } from '@/types';
import {
  Permission,
  PermissionState,
} from '@/types/permissions.types';
import { Form, Steps } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowRight, FaSave } from 'react-icons/fa';
import {
  NotesAccountStep,
  PersonalDetailsStep,
  RegionTeamStep,
  RolePermissionsStep,
} from './forms';

interface UserFormDrawerProps {
  open: boolean;
  currentUserRole?: any;
  onClose: () => void;
  onSubmit: (userData: any) => void;
  isRegionAll?: boolean;
  userRegion?: number;
}

export const UserFormDrawer: React.FC<UserFormDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  isRegionAll = true,
  userRegion,
}) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [customPermissions, setCustomPermissions] = useState<Permission[]>([]);
  const [formData, setFormData] = useState<any>({}); // Store accumulated form data
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API Hooks
  const createUserMutation = useCreateUser();
  const assignRoleMutation = useAssignRole();
  const createAppRoleMutation = useCreateAppRole();

  // Fetch roles from API
  const { data: rolesData } = useAppRoleList({
    queryParams: { current: 0, pageSize: 100 },
    enabled: open,
  });

  // Default business unit ID (from env)
  const DEFAULT_BUSINESS_UNIT_ID = APPENV.DEFAULT_BUSINESS_UNIT_ID;

  // Watch form values for conditional rendering
  const selectedRoleId = Form.useWatch('role', form) as string | undefined;

  // Get base permissions from selected role's dogma_permissionsjson
  const basePermissions = useMemo(() => {
    if (!selectedRoleId || !rolesData?.value) return [];

    const selectedRoleData = rolesData.value.find(
      (role) => role.dogma_approleid === selectedRoleId
    );

    if (!selectedRoleData || !selectedRoleData.dogma_permissionsjson) return [];

    try {
      const permissionsJson: PermissionState = JSON.parse(
        selectedRoleData.dogma_permissionsjson
      );

      const enabledPermissions: Permission[] = [];

      Object.entries(permissionsJson).forEach(([permissionId, permValue]: [string, any]) => {
        // Handle both old format {enabled, modified} and new format (boolean)
        const isEnabled = typeof permValue === 'boolean' ? permValue : permValue?.enabled;

        if (isEnabled === true) {
          enabledPermissions.push(permissionId as Permission);
        }
      });

      return enabledPermissions;
    } catch (error) {
      return [];
    }
  }, [selectedRoleId, rolesData]);

  // Define steps
  const steps = [
    {
      title: 'Personal Details',
      content: <PersonalDetailsStep />,
    },
    {
      title: 'Role & Permissions',
      content: (
        <RolePermissionsStep
          selectedRole={selectedRoleId}
          basePermissions={basePermissions}
          customPermissions={customPermissions}
          onToggleCustomPermission={toggleCustomPermission}
        />
      ),
    },
    {
      title: 'Region & Team',
      content: <RegionTeamStep selectedRole={selectedRoleId} isRegionAll={isRegionAll} userRegion={userRegion} />,
    },
    {
      title: 'Notes & Setup',
      content: <NotesAccountStep isEditing={false} />,
    },
  ];

  const isLastStep = currentStep === steps.length - 1;

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (open) {
      // Initialize form with default values for new user
      const initialData = {
        region: 3, // England - North (REGION_ENGLAND_NORTH)
        team: 1, // Internal Sales Team
        sendWelcomeEmail: true,
        generatePassword: true,
      };
      form.setFieldsValue(initialData);
      setFormData(initialData);
      setCustomPermissions([]);
      setCurrentStep(0);
    } else {
      // Reset when closed
      form.resetFields();
      setFormData({});
      setCustomPermissions([]);
      setCurrentStep(0);
    }
  }, [open, form]);

  // Restore form values from state when step changes
  useEffect(() => {
    if (open && Object.keys(formData).length > 0) {
      form.setFieldsValue(formData);
    }
  }, [currentStep, open, formData, form]);

  // Handle role change
  const handleRoleChange = (changedValues: any) => {
    if (changedValues.role) {
      // Reset custom permissions when role changes
      setCustomPermissions([]);
    }
  };

  // Toggle custom permission
  function toggleCustomPermission(permission: Permission) {
    setCustomPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      }
      return [...prev, permission];
    });
  }

  // Handle form submission or next step
  const handleFormSubmit = async () => {
    try {
      // Validate current step fields
      await form.validateFields();

      // Get current form values and merge with accumulated data
      const currentValues = form.getFieldsValue();
      const updatedFormData = { ...formData, ...currentValues };
      setFormData(updatedFormData);

      if (isLastStep) {
        // Final submission - use accumulated form data
        setIsSubmitting(true);

        try {
          // Step 1: Create the user
          const createUserPayload = {
            domainname: updatedFormData.email,
            "internalemailaddress": updatedFormData.email,
            firstname: updatedFormData.firstName,
            lastname: updatedFormData.lastName,
            dogma_region: updatedFormData?.dogma_region,
            dogma_team: updatedFormData?.dogma_team,
            "businessunitid@odata.bind": `/businessunits(${DEFAULT_BUSINESS_UNIT_ID})`
          };
          const createdUser = await createUserMutation.mutateAsync(createUserPayload);

          // Get the systemuserid from created user
          const systemUserId = createdUser.systemuserid;


          // Step 2: Assign the selected role to the user
          if (updatedFormData.role) {
            const assignRolePayload = {
              systemuserid: systemUserId,
              roleid: updatedFormData.role,
            };
            await assignRoleMutation.mutateAsync(assignRolePayload);
          }

          // Step 3: If there are custom permissions, create a custom role
          if (customPermissions.length > 0) {
            // Build permissions JSON for custom role
            const rolePermissions: {
              [permission: string]: boolean;
            } = {};

            // Get all permissions from base role
            basePermissions.forEach((perm) => {
              rolePermissions[perm] = true;
            });

            // Add custom permissions
            customPermissions.forEach((perm) => {
              rolePermissions[perm] = true;
            });

            const permissionState: PermissionState = {
              ...rolePermissions,
            };

            const customRoleName = `${updatedFormData.firstName} ${updatedFormData.lastName} - Custom Role`;
            const createCustomRolePayload = {
              dogma_defaultsecurityrole: false,
              dogma_name: customRoleName,
              dogma_permissionsjson: JSON.stringify(permissionState),
            };

            const createdCustomRole = await createAppRoleMutation.mutateAsync(createCustomRolePayload);

            // Step 4: Assign the custom role to the user
            const assignCustomRolePayload = {
              systemuserid: systemUserId,
              roleid: createdCustomRole.dogma_approleid,
            };
            await assignRoleMutation.mutateAsync(assignCustomRolePayload);
          }

          // Success! Call onSubmit callback
          onSubmit(updatedFormData);
          onClose();
        } catch (error) {
          // Error is already handled by the mutation hooks with toast
        } finally {
          setIsSubmitting(false);
        }
      } else {
        // Move to next step
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
    }
  };

  // Handle back button
  const handleBack = () => {
    if (currentStep > 0) {
      // Save current step data before going back
      const currentValues = form.getFieldsValue();
      const updatedFormData = { ...formData, ...currentValues };
      setFormData(updatedFormData);

      // Go back to previous step
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle close (cancel button)
  const handleClose = () => {
    // Close drawer
    onClose();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleRoleChange}
      onFinish={handleFormSubmit}
    >
      <Drawer
        open={open}
        onClose={handleClose}
        title="Add New User"
        subTitle="Create a new user account with role-based permissions"
        placement={PlacementEnum.bottom}
        size={DrawerSizeEnum.large}
        width="800px"
        saveBtnText={isLastStep ? 'Create User' : 'Next'}
        saveBtnIcon={isLastStep ? <FaSave /> : <FaArrowRight />}
        showBackBtn={currentStep > 0}
        backBtnText="Back"
        onBack={handleBack}
        loading={isSubmitting}
        disableButton={isSubmitting}
      >
        <div className='py-6'>
          {/* Steps Navigation */}
          <Steps
            current={currentStep}
            items={steps.map((step) => ({ title: step.title }))}
            className="mb-8"
          />

          {/* Step Content */}
          <div className="min-h-[400px] py-6">{steps[currentStep]?.content}</div>
        </div>
      </Drawer>
    </Form>
  );
};

export default UserFormDrawer;
